import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import { Page, PageActions } from 'components';
import { useFields, useOrganizations, useProperties } from 'hooks';
import { FieldMap, FieldSideBar, FieldSideBarDraw, FieldSideBarEdit, FieldSideBarUpload } from './components';
import { Box } from '@material-ui/core';
import _ from 'lodash';
import { DashboardHelpers } from 'utils';

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: theme.spacing(2),
  },
  root: {
    padding: 0,
  },
}));

const FieldsPage = () => {
  const classes = useStyles();
  const pageProps = {};
  const { t } = useTranslation();
  const [editingField, updateField] = useState();
  const [action, setAction] = useState('edit');
  const [map, setMap] = useState();
  const polyRef = useRef();
  const { selectedOrganizationId } = useOrganizations();
  const { selectedPropertyId } = useProperties();
  const { selectedField, saveField, selectedFieldId, deleteField, setSelectedField } = useFields();

  const [formErrors, setFormErrors] = useState({
    name: '',
    draw: '',
    geometry: '',
    crop_id: '',
    soil_density: '',
    soil_texture: '',
  });

  const [currentOrganization, setCurrentOrganization] = useState(selectedOrganizationId);
  const [currentProperty, setCurrentProperty] = useState(selectedPropertyId);
  const [currentField, setCurrentField] = useState(selectedFieldId);
  const [zooming, setZooming] = useState(false);

  useEffect(() => {
    if (zooming && editingField) {
      updateField({ ...editingField, editPolygon: false });
    }
  }, [zooming]);

  useEffect(() => {
    if (!selectedField?.id) {
      updateField();
    }
  }, [selectedField]);

  if (
    currentOrganization !== selectedOrganizationId ||
    currentProperty !== selectedPropertyId ||
    currentField !== selectedFieldId
  ) {
    try {
      if (map?.editTools) {
        map.removeLayer(editingField.geometry);
        map.editTools.stopDrawing();
      }
    } catch {
      polyRef.current = null;
    }
    if (polyRef.current) {
      polyRef.current.remove();
      polyRef.current = null;
    }
    if (editingField?.draw) {
      setAction('edit');
      updateField();
    }

    setCurrentOrganization(selectedOrganizationId);
    setCurrentProperty(selectedPropertyId);
    setCurrentField(selectedFieldId);
  }

  const title = 'field_plural';
  pageProps.actionFab = true;

  const onSave = () => {
    console.log('saving field:', editingField);
    if (formDataValid()) {
      if (
        editingField.property_id === DashboardHelpers.StatusCodes.NoPropertySelected ||
        editingField.property_id === undefined
      ) {
        editingField.property_id = selectedPropertyId;
      }
      const finalField = {
        ...editingField,
        editPolygon: undefined,
        draw: undefined,
        organization_id: selectedOrganizationId,
      };
      saveField(finalField);
      if (finalField?.id) {
        setSelectedField(finalField.id);
      }
      if (map?.editTools) {
        map.removeLayer(editingField.geometry);
        map.editTools.stopDrawing();
      }
      updateField();
      clearEdits();
    }
  };

  const formDataValid = () => {
    let valid = true;
    let formErrorBuilder = formErrors;

    if (editingField) {
      // check for display name existing if updating field
      formErrorBuilder = DashboardHelpers.validateFormField(
        editingField,
        'name',
        formErrorBuilder,
        t('form_field_needs') + t('name')
      );
    }

    setFormErrors({ ...formErrorBuilder });

    valid =
      _.filter(formErrorBuilder, (val) => {
        return val !== '';
      }).length > 0
        ? false
        : true;
    return valid;
  };

  const updateFieldGeometry = (geometry, layer) => {
    if (polyRef.current === null) {
      polyRef.current = layer;
    }
    if (geometry && editingField) {
      updateField({ ...editingField, geometry });
    } else if (editingField) {
      updateField({ ...editingField, geometry: undefined });
    }
  };

  const editPolygon = () => {
    updateField({ ...editingField, editPolygon: true });
    setZooming(false);
  };

  const clearEdits = () => {
    try {
      if (map?.editTools) {
        map.editTools.stopDrawing();
        map.removeLayer(editingField.geometry);
      }
    } catch {
      polyRef.current = null;
    }
    if (polyRef.current) {
      polyRef.current.remove();
      polyRef.current = null;
    }
    setAction('edit');
    setSelectedField();
    updateField();
  };

  return (
    <Box>
      <Page
        title={t(title)}
        className={classes.root}
        pageActions={
          <PageActions>
            {!editingField && <FieldSideBar action={action} setAction={setAction} updateField={updateField} />}
            {editingField?.draw && !selectedField && (
              <FieldSideBarDraw
                onSave={onSave}
                onPrev={clearEdits}
                field={editingField}
                errors={formErrors}
                setValues={updateField}
                onCancel={clearEdits}
              />
            )}
            {editingField?.draw === false && (
              <FieldSideBarUpload
                onSave={onSave}
                onPrev={clearEdits}
                field={editingField}
                errors={formErrors}
                setValues={updateField}
                onCancel={clearEdits}
              />
            )}
            {editingField?.id && selectedField && (
              <FieldSideBarEdit
                onSave={onSave}
                onPrev={clearEdits}
                field={editingField}
                errors={formErrors}
                setValues={updateField}
                editPolygon={editPolygon}
                selectedFieldId={selectedFieldId}
                deleteField={deleteField}
                updateField={updateField}
              />
            )}
          </PageActions>
        }
      >
        <FieldMap
          polyRef={polyRef}
          editingField={editingField}
          updateFieldGeometry={updateFieldGeometry}
          setMap={setMap}
          setZooming={setZooming}
        />
      </Page>
    </Box>
  );
};

export default FieldsPage;
